import { useMemo } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { useCurrentStateAndParams } from '@uirouter/react';
import { isEqual } from 'lodash';

import {
  type AddonsAddonAccessUpdatePayload,
  type AddonsAddonRecord,
  type PortainerTeamAccessPolicies,
} from '@api/types.gen';

import { notifySuccess } from '@/portainer/services/notifications';
import { useApplyDenyPortainerAccessMutation } from '@/react/portainer/users/teams/queries/useApplyDenyPortainerAccessMutation';
import {
  getTeamsToApplyDenyPortainerAccess,
  useTeamsDenyingPortainerAccess,
} from '@/react/portainer/users/teams/queries/useTeamsDenyingPortainerAccess';
import { useHasEnabledAddon } from '@/react/portainer/addons/queries/useHasEnabledAddon';

import { Widget, WidgetBody } from '@@/Widget';
import { LoadingButton } from '@@/buttons';

import { useAddon } from '../queries/useAddon';
import { useUpdateAddonAccess } from '../queries/useUpdateAddonAccess';
import { AssignTeamsStep } from '../InstallView/AssignTeamsStep';

interface FormValues {
  teams: number[];
  /** Per-team override, keyed by team id; a team absent here keeps its current state. */
  denyPortainerAccess: Record<number, boolean>;
}

export function UserAccessTab() {
  const { params } = useCurrentStateAndParams();
  const addonId = String(params.id ?? '');

  const addonQuery = useAddon(addonId);
  const updateAccess = useUpdateAddonAccess(addonId);
  const applyDenyPortainerAccess = useApplyDenyPortainerAccessMutation();
  // Any enabled add-on is a landing target, not just this one.
  const showDenyPortainerAccess = useHasEnabledAddon();
  const denyingTeamIds = useTeamsDenyingPortainerAccess();
  const record = addonQuery.data?.record;

  const initialTeams = useMemo<number[]>(
    () => Object.keys(record?.teamAccessPolicies ?? {}).map(Number),
    [record]
  );

  // The body waits on the addon load: an early save with the grants unknown
  // would submit a partial list and revoke everyone's access.
  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget aria-label="User access">
          <WidgetBody loading={addonQuery.isLoading}>
            <Formik<FormValues>
              enableReinitialize
              initialValues={{
                teams: initialTeams,
                denyPortainerAccess: {},
              }}
              onSubmit={handleSubmit}
            >
              {({ values, isSubmitting }) => {
                const teamsChanged = !isEqual(
                  [...values.teams].sort(),
                  [...initialTeams].sort()
                );
                const hasTeamsToUpdate =
                  teamsToUpdate(values).length > 0 && showDenyPortainerAccess;

                return (
                  <Form noValidate>
                    <AssignTeamsStep
                      showDenyPortainerAccess={showDenyPortainerAccess}
                    />
                    <div className="mt-4 flex">
                      <LoadingButton
                        className="!ml-0"
                        isLoading={isSubmitting}
                        loadingText="Saving..."
                        disabled={!teamsChanged && !hasTeamsToUpdate}
                        data-cy="addon-user-access-save-button"
                      >
                        Save
                      </LoadingButton>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );

  function teamsToUpdate(values: FormValues) {
    return getTeamsToApplyDenyPortainerAccess(
      values.teams,
      denyingTeamIds,
      values.denyPortainerAccess
    );
  }

  async function handleSubmit(
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) {
    try {
      await updateAccess.mutateAsync(toPayload(values.teams, record));

      const updates = showDenyPortainerAccess ? teamsToUpdate(values) : [];
      if (updates.length > 0) {
        await applyDenyPortainerAccess.mutateAsync({ updates });
      }

      notifySuccess('Success', 'Add-on access updated');
    } catch {
      // Errors already surfaced via each mutation's toast.
    } finally {
      setSubmitting(false);
    }
  }
}

// Addon access is a flat allow-list: the gateway only checks for presence in
// these maps (see AuthorizedAddonAccess in api/http/security/authorization.go),
// so RoleId carries no meaning.
const NO_ROLE = 0;

// This tab edits team access only, so the record's user grants pass through
// unchanged rather than being dropped.
function toPayload(
  teams: number[],
  record: AddonsAddonRecord | undefined
): AddonsAddonAccessUpdatePayload {
  const teamAccessPolicies: PortainerTeamAccessPolicies = {};
  teams.forEach((id) => {
    teamAccessPolicies[id] = { RoleId: NO_ROLE };
  });

  return {
    userAccessPolicies: record?.userAccessPolicies ?? {},
    teamAccessPolicies,
  };
}
